import { Fragment } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import {
    FileText,
    ClipboardCheck,
    UserCheck,
    Users,
    MessageSquare,
    Scale,
    X,
    RefreshCw,
    RotateCw,
    BookOpen,
    Type,
    Eye,
    Printer,
    Globe,
    ChevronRight
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'


type Actor = 'author' | 'editorial' | 'reviewers' | 'production'

const ACTORS: Record<Actor, { label: string; color: string; soft: string }> = {
    author: { label: 'Author', color: '#8a6414', soft: 'rgba(138,100,20,0.07)' },
    editorial: { label: 'Editorial Office', color: '#9e2b25', soft: 'rgba(158,43,37,0.06)' },
    reviewers: { label: 'Reviewers', color: '#545148', soft: 'rgba(84,81,72,0.07)' },
    production: { label: 'Production', color: '#2f6b46', soft: 'rgba(47,107,70,0.07)' }
}

const TONES = {
    reject: { color: '#9e2b25', bg: 'rgba(158,43,37,0.07)' },
    loop: { color: '#8a6414', bg: 'rgba(138,100,20,0.07)' },
    accept: { color: '#2f6b46', bg: 'rgba(47,107,70,0.07)' }
}

function Connector({ label, labelColor }: { label?: string; labelColor?: string }) {
    const reduced = useReducedMotion()
    return (
        <div className="relative flex justify-center" style={{ height: '3.5rem' }} aria-hidden>
            <svg width="16" height="56" style={{ overflow: 'visible' }}>
                <line x1="8" y1="0" x2="8" y2="48" stroke="var(--color-rule)" strokeWidth="1.5" />
                {!reduced && (
                    <motion.line
                        x1="8"
                        y1="0"
                        x2="8"
                        y2="48"
                        stroke="var(--color-gold-400)"
                        strokeWidth="1.5"
                        strokeDasharray="3 9"
                        animate={{ strokeDashoffset: [0, -24] }}
                        transition={{ repeat: Infinity, duration: 1.5, ease: 'linear' }}
                    />
                )}
                <polygon points="3.5,48 12.5,48 8,55" fill="var(--color-gold-500)" />
                {!reduced && (
                    <motion.circle
                        cx="8"
                        r="2.6"
                        fill="var(--color-gold-500)"
                        animate={{ cy: [3, 45], opacity: [0, 1, 1, 0] }}
                        transition={{ repeat: Infinity, duration: 1.5, ease: 'linear', repeatDelay: 0.9 }}
                    />
                )}
            </svg>
            {label && (
                <span
                    className="absolute top-1/2 -translate-y-1/2 left-1/2 ml-5 whitespace-nowrap text-[9.5px] uppercase font-semibold px-2.5 py-1"
                    style={{
                        letterSpacing: '0.14em',
                        color: labelColor ?? 'var(--color-ink-400)',
                        background: 'var(--color-paper-100)',
                        border: `1px solid ${labelColor ?? 'var(--color-rule)'}`,
                        borderRadius: '999px'
                    }}
                >
                    {label}
                </span>
            )}
        </div>
    )
}

function NodeCard({
    icon: Icon,
    title,
    desc,
    actor,
    decision = false
}: {
    icon: LucideIcon
    title: string
    desc: string
    actor: Actor
    decision?: boolean
}) {
    const a = ACTORS[actor]
    return (
        <motion.div
            initial={{ opacity: 0, y: 18, scale: 0.98 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
            whileHover={{ y: -3, boxShadow: '0 18px 44px -18px rgba(22,20,16,0.35)' }}
            className="relative mx-auto w-full"
            style={{
                maxWidth: '30rem',
                background: 'var(--color-paper-50)',
                border: '1px solid var(--color-rule)',
                borderLeft: `3px solid ${a.color}`,
                padding: '1rem 1.15rem',
                boxShadow: '0 10px 30px -24px rgba(22,20,16,0.3)'
            }}
        >
            <div className="flex items-start gap-3.5">
                {decision ? (
                    <span className="shrink-0 flex items-center justify-center" style={{ width: '2.5rem', height: '2.5rem' }}>
                        <span
                            className="flex items-center justify-center"
                            style={{
                                width: '1.8rem',
                                height: '1.8rem',
                                transform: 'rotate(45deg)',
                                background: a.soft,
                                border: `1.5px solid ${a.color}`
                            }}
                        >
                            <Icon size={15} style={{ transform: 'rotate(-45deg)', color: a.color }} />
                        </span>
                    </span>
                ) : (
                    <span
                        className="shrink-0 flex items-center justify-center"
                        style={{
                            width: '2.5rem',
                            height: '2.5rem',
                            background: a.soft,
                            border: `1px solid ${a.color}`,
                            borderRadius: '6px'
                        }}
                    >
                        <Icon size={17} style={{ color: a.color }} />
                    </span>
                )}
                <div>
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                        <span
                            className="font-display"
                            style={{ fontSize: '1.05rem', fontWeight: 500, color: 'var(--color-ink-700)', letterSpacing: '-0.01em' }}
                        >
                            {title}
                        </span>
                        <span className="text-[9px] uppercase font-semibold" style={{ letterSpacing: '0.16em', color: a.color }}>
                            {a.label}
                        </span>
                    </div>
                    <p className="mt-1 text-[13px] leading-relaxed" style={{ color: 'var(--color-ink-400)' }}>
                        {desc}
                    </p>
                </div>
            </div>
        </motion.div>
    )
}

function BranchChip({
    tone,
    icon: Icon,
    label,
    sub,
    spin = false,
    index = 0
}: {
    tone: keyof typeof TONES
    icon: LucideIcon
    label: string
    sub?: string
    spin?: boolean
    index?: number
}) {
    const t = TONES[tone]
    const reduced = useReducedMotion()
    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.35, delay: 0.15 + index * 0.1 }}
            whileHover={{ y: -2 }}
            className="flex items-center gap-2 px-3 py-1.5"
            style={{ background: t.bg, border: `1px solid ${t.color}`, borderRadius: '3px' }}
        >
            {spin && !reduced ? (
                <motion.span className="flex" animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 6, ease: 'linear' }}>
                    <Icon size={13} style={{ color: t.color }} />
                </motion.span>
            ) : (
                <Icon size={13} style={{ color: t.color }} />
            )}
            <span className="leading-tight text-left">
                <span className="block text-[10.5px] font-semibold uppercase" style={{ letterSpacing: '0.08em', color: t.color }}>
                    {label}
                </span>
                {sub && (
                    <span className="block text-[10px]" style={{ color: 'var(--color-ink-400)' }}>
                        {sub}
                    </span>
                )}
            </span>
        </motion.div>
    )
}

function BranchRow({ children }: { children: React.ReactNode }) {
    return <div className="mt-3 flex flex-wrap justify-center gap-2.5 px-2">{children}</div>
}

function PhaseLabel({ n, title }: { n: string; title: string }) {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-4 mx-auto my-8"
            style={{ maxWidth: '30rem' }}
        >
            <span className="flex-1 h-px" style={{ background: 'var(--color-rule)' }} />
            <span
                className="text-[10px] uppercase font-semibold whitespace-nowrap"
                style={{ letterSpacing: '0.2em', color: 'var(--color-gold-700)' }}
            >
                Phase {n} · {title}
            </span>
            <span className="flex-1 h-px" style={{ background: 'var(--color-rule)' }} />
        </motion.div>
    )
}

const PRODUCTION_STEPS: { icon: LucideIcon; label: string; sub: string }[] = [
    { icon: Type, label: 'Typesetting', sub: 'Article laid out for the issue' },
    { icon: Eye, label: 'Proofreading', sub: 'Final proofs checked' },
    { icon: Printer, label: 'Publishing', sub: 'Prepared for release' }
]

function ProductionRow() {
    const reduced = useReducedMotion()
    return (
        <div className="mx-auto flex flex-col sm:flex-row items-stretch justify-center gap-2" style={{ maxWidth: '34rem' }}>
            {PRODUCTION_STEPS.map((s, i) => (
                <Fragment key={s.label}>
                    {i > 0 && (
                        <div className="flex items-center justify-center" aria-hidden>
                            <motion.span
                                className="flex rotate-90 sm:rotate-0"
                                animate={reduced ? undefined : { opacity: [0.35, 1, 0.35] }}
                                transition={{ repeat: Infinity, duration: 1.4, ease: 'easeInOut', delay: i * 0.35 }}
                            >
                                <ChevronRight size={16} style={{ color: 'var(--color-gold-500)' }} />
                            </motion.span>
                        </div>
                    )}
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: i * 0.12 }}
                        whileHover={{ y: -3 }}
                        className="flex-1 text-center px-4 py-3.5"
                        style={{ background: 'var(--color-paper-50)', border: '1px solid var(--color-rule)', borderTop: `2px solid ${ACTORS.production.color}` }}
                    >
                        <s.icon size={16} className="mx-auto" style={{ color: ACTORS.production.color }} />
                        <div className="mt-1.5 font-display" style={{ fontSize: '0.95rem', fontWeight: 500, color: 'var(--color-ink-700)' }}>
                            {s.label}
                        </div>
                        <div className="text-[10.5px] mt-0.5" style={{ color: 'var(--color-ink-400)' }}>
                            {s.sub}
                        </div>
                    </motion.div>
                </Fragment>
            ))}
        </div>
    )
}

function PublishedNode() {
    const reduced = useReducedMotion()
    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            className="relative mx-auto text-center px-8 py-6"
            style={{
                maxWidth: '24rem',
                background: '#2f6b46',
                color: 'var(--color-paper-50)',
                boxShadow: '0 22px 50px -20px rgba(47,107,70,0.55)'
            }}
        >
            {!reduced && (
                <motion.span
                    aria-hidden
                    className="absolute inset-0"
                    style={{ border: '1px solid #2f6b46' }}
                    animate={{ scale: [1, 1.06, 1.1], opacity: [0.7, 0.25, 0] }}
                    transition={{ repeat: Infinity, duration: 2.2, ease: 'easeOut' }}
                />
            )}
            <Globe size={20} className="mx-auto" />
            <div className="mt-2 font-display" style={{ fontSize: '1.3rem', fontWeight: 500, letterSpacing: '-0.01em' }}>
                Published
            </div>
            <p className="mt-1.5 text-[12.5px] leading-relaxed" style={{ color: 'rgba(253,251,246,0.82)' }}>
                The paper appears in the next open-access issue and is publicly available to read and cite.
            </p>
        </motion.div>
    )
}

function Legend() {
    return (
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-10">
            {(Object.keys(ACTORS) as Actor[]).map(k => (
                <span key={k} className="inline-flex items-center gap-2 text-[10.5px] uppercase font-semibold" style={{ letterSpacing: '0.12em', color: 'var(--color-ink-400)' }}>
                    <span aria-hidden style={{ width: 9, height: 9, borderRadius: '50%', background: ACTORS[k].color }} />
                    {ACTORS[k].label}
                </span>
            ))}
        </div>
    )
}

export default function AnimatedFlowchart() {
    const reduced = useReducedMotion()

    return (
        <div>
            <Legend />

            <PhaseLabel n="01" title="Submission" />
            <NodeCard
                icon={FileText}
                title="Manuscript Submitted"
                desc="The author submits the manuscript through the editorial portal with all files and the cover letter."
                actor="author"
            />
            <Connector />

            <PhaseLabel n="02" title="Editorial Screening" />
            <NodeCard
                icon={ClipboardCheck}
                title="Initial Check"
                desc="The editorial office checks the format, runs plagiarism screening and requests any missing data from the author or co-author."
                actor="editorial"
            />
            <Connector />
            <NodeCard
                icon={UserCheck}
                title="Editor Assigned"
                desc="The Editor-in-Chief assigns a handling editor to steer the manuscript through review."
                actor="editorial"
            />
            <Connector />
            <NodeCard
                icon={Scale}
                title="Data Complete?"
                desc="The handling editor verifies that the submission is complete and within the journal's scope."
                actor="editorial"
                decision
            />
            <BranchRow>
                <BranchChip tone="loop" icon={RotateCw} label="Incomplete" sub="Returned to the author" index={0} />
            </BranchRow>
            <Connector label="Yes" labelColor="#2f6b46" />
            <NodeCard
                icon={ClipboardCheck}
                title="Initial Review"
                desc="The editor checks the data, scores the manuscript and prepares an initial review report."
                actor="editorial"
                decision
            />
            <BranchRow>
                <BranchChip tone="reject" icon={X} label="Reject" sub="Does not proceed to review" index={0} />
            </BranchRow>
            <Connector label="Proceeds" labelColor="#2f6b46" />

            <PhaseLabel n="03" title="Peer Review" />
            <div className="relative mx-auto" style={{ maxWidth: '30rem' }}>
                <div
                    aria-hidden
                    className="absolute hidden lg:block"
                    style={{ left: '-3rem', top: '2rem', bottom: '4.5rem', width: '1rem' }}
                >
                    <svg width="16" height="100%" style={{ overflow: 'visible' }}>
                        <polygon points="3.5,9 12.5,9 8,1" fill={TONES.loop.color} />
                        <motion.line
                            x1="8"
                            y1="9"
                            x2="8"
                            y2="100%"
                            stroke={TONES.loop.color}
                            strokeWidth="1.5"
                            strokeDasharray="3 8"
                            animate={reduced ? undefined : { strokeDashoffset: [0, 22] }}
                            transition={{ repeat: Infinity, duration: 1.7, ease: 'linear' }}
                        />
                    </svg>
                    <span
                        className="absolute top-1/2 left-1/2 whitespace-nowrap text-[9px] uppercase font-semibold px-2"
                        style={{
                            letterSpacing: '0.18em',
                            color: TONES.loop.color,
                            background: 'var(--color-paper-soft)',
                            transform: 'translate(-50%, -50%) rotate(-90deg)'
                        }}
                    >
                        Revision loop
                    </span>
                </div>

                <NodeCard
                    icon={Users}
                    title="Reviewers Assigned"
                    desc="The paper is sent to independent reviewers for double-blind peer review — neither side knows the other's identity."
                    actor="reviewers"
                />
                <Connector />
                <NodeCard
                    icon={MessageSquare}
                    title="Review Reports"
                    desc="Reviewers return their reports and recommendations to the editorial team, and revised files are reviewed again."
                    actor="reviewers"
                />
                <Connector />
                <NodeCard
                    icon={Scale}
                    title="Editorial Decision"
                    desc="The Editor-in-Chief weighs the review summary and takes the final decision on the manuscript."
                    actor="editorial"
                    decision
                />
                <BranchRow>
                    <BranchChip tone="reject" icon={X} label="Reject" index={0} />
                    <BranchChip tone="loop" icon={RefreshCw} label="To Be Revised" sub="Returns to peer review" spin index={1} />
                    <BranchChip tone="loop" icon={RotateCw} label="To Be Resubmitted" sub="Returns to peer review" spin index={2} />
                </BranchRow>
            </div>
            <Connector label="Accepted" labelColor="#2f6b46" />

            <PhaseLabel n="04" title="Production" />
            <NodeCard
                icon={BookOpen}
                title="Paper in Journal's Template"
                desc="The accepted manuscript is set in the journal's template ahead of production."
                actor="production"
            />
            <Connector />
            <ProductionRow />
            <Connector />
            <PublishedNode />
        </div>
    )
}
